import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import _ from 'underscore';

import { Messages } from '../../../../codegen/Localize';
import { SortTool } from '../../../../common/react/components/SortTool';
import { getQueryStringServiceFromAngular } from '../../../../common/react/QueryString';
import { Order } from '../../../../ts/helpers/Order';
import { ContentSortOptions } from '../../../../ts/models/ContentSortOptions';
import { ExplorableItemType } from '../../../../ts/models/ContentType';
import { OrderKeys } from '../../../../ts/models/OrderKeys';
import { contentSortOptionsByCatalogContentType } from '../../../../ts/models/sort/CatalogSortOptions';
import { dataKeyToCrassFieldMap } from '../../../../ts/models/sort/MixedContentInCrassSortOptions';
import { getDefaultOrderForContentTypesOrSpaceInfo, removeInvalidSortClauses } from '../../../../ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { Sorter, SubscriptionBehavior } from '../../../../content/Sorter';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { setOrderAction } from '../../../actions/SetOrderAction';
import { AddFieldToSortOrderAction } from '../../../actions/spaces/AddFieldToSortOrderAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { SelectSortOrderAction } from '../../../actions/spaces/SelectSortOrderAction';
import { SortContentAction } from '../../../actions/spaces/SortContentAction';
import { getAppStore } from '../../../AppStore';
import { selectCurrentlyApplicableContentTypes } from '../../../selectors/ContentFilterSelectors';
import { selectContentSort } from '../../../selectors/ContentSortSelectors';
import { selectSpaceInfo } from '../../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { isCatalogSearchSpace, isCatalogSpace, isFavoritesSpace, isRecentsSpace, isUnifiedSearchSpace, SpaceInfo, subspaceNameToCatalogContentType } from '../../../state/SpaceInfo';
import { UrlParamKeys } from '../../../UrlParamKeys';

// We want to use the url as the source of truth
// So we grab the url order key and remove incorrect order keys
// and also remove any inapplicable order keys to our current space
// If we have no order keys left we apply the default sort order for current space
// Then finally if the applied/correct sort order doesn't match the url, update the url
export function updateOrderQueryParamsIfMismatched(
  contentTypes: ReadonlyArray<ExplorableItemType>,
  spaceInfo: SpaceInfo)
{
  const queryString = getQueryStringServiceFromAngular();
  const currentSortQuery = queryString()[UrlParamKeys.Order];
  const orderFromQuery: ReadonlyArray<IOrderClause> = _.isUndefined(currentSortQuery) ? [] : Order.fromInput(currentSortQuery);
  let expectedOrder = orderFromQuery;

  if (!orderFromQuery || orderFromQuery.length === 0) {
    expectedOrder = selectContentSort(getAppStore().getState());
  }

  expectedOrder = removeInvalidSortClauses(expectedOrder, contentTypes, spaceInfo);

  // use default order if we dont have any valid order from our current query
  if (expectedOrder.length === 0){
    expectedOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
  }

  getAppStore().dispatch(setOrderAction(expectedOrder));

  if (!_.isEqual(orderFromQuery, expectedOrder)) {
    // the false here doesn't update or reload the angular scope so sort watchers won't get hit
    updateOrderQueryParam(expectedOrder, contentTypes, spaceInfo, false);
  }
}

export function updateOrderQueryParam(order: ReadonlyArray<IOrderClause>,
                                      contentTypes: ReadonlyArray<ExplorableItemType>,
                                      spaceInfo: SpaceInfo,
                                      scopeApply: boolean = true) {
  const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
  const sorter = new Sorter(Array.from(defaultOrder), SubscriptionBehavior.None);
  sorter.updateLocationSearch(order);

  if (scopeApply) {
    // when updating the url outside Angular, $apply is needed to update the window location.
    ServiceLocator.getAngularActionContext().scope.$apply();
  }
}

export function* processSortContent(action: SortContentAction): SagaIterator {
  try {
    const currentSortOrder = yield select(selectContentSort);

    if (!_.isEqual(action.order, currentSortOrder)) {
      yield put(setOrderAction(action.order));
      yield put(requestSpaceContents());
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* processSelectSortOrder(action: SelectSortOrderAction): SagaIterator {
  try {
    const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
    const spaceInfo = yield select(selectSpaceInfo);
    yield call(updateOrderQueryParam, action.order, contentTypes, spaceInfo);
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* processAddFieldToSortOrder(action: AddFieldToSortOrderAction): SagaIterator {
  try {
    const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
    const spaceInfo = yield select(selectSpaceInfo);
    const existingOrder: ReadonlyArray<IOrderClause> = yield select(selectContentSort);

    let field = action.field;
    if (isUnifiedSearchSpace(spaceInfo)) {
      field = dataKeyToCrassFieldMap[action.field] || field;
    }

    const newOrder = promoteFieldToPrimarySort(spaceInfo, field, existingOrder);

    yield call(updateOrderQueryParam, newOrder, contentTypes, spaceInfo);
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}


export function promoteFieldToPrimarySort(spaceInfo: SpaceInfo, field: string, existingOrder: ReadonlyArray<IOrderClause>): IOrderClause[] {

  // position order is only valid as the primary field, (due to how APIs and cores are implemented)
  // So remove position order when promoting another field to primary sort
  if (isFavoritesSpace(spaceInfo) && field !== OrderKeys.position) {
    existingOrder =  _.filter(existingOrder, orderClause => orderClause.field !== OrderKeys.position);
  }

  // lastAccessedAt order is only valid as the primary field, (due to how APIs and cores are implemented)
  // So remove lastAccessedAt order when promoting another field to primary sort
  // Or remove all other sorts when promoting lastAccessedAt
  if (isRecentsSpace(spaceInfo)) {
    if(field === OrderKeys.lastAccessedAt) {
      existingOrder =  _.filter(existingOrder, orderClause => orderClause.field === OrderKeys.lastAccessedAt);
    } else {
      existingOrder =  _.filter(existingOrder, orderClause => orderClause.field !== OrderKeys.lastAccessedAt);
    }
  }

  const existingClause = Order.findOrderClauseForField(existingOrder, field);
  let newOrder: IOrderClause[];

  if (existingClause) {
    if (Order.startsWithField(existingOrder, field)) {

      // If the field is already the primary sort order, toggle the direction (asc/desc).
      newOrder = Order.withFirstClauseToggled(existingOrder);
    } else {
      // If the field is used but not primary, move it up to and dont toggle the direction (asc/desc).
      newOrder = _.reject(existingOrder, clause => clause.field === field);
      newOrder = Order.withClausePrepended(newOrder, existingClause);
    }
  } else {
    // If the field is not used at all currently, add it as the primary order clause.
    // NOTE: Order.MaxLength determines how many clauses are included before they drop off.
    const option = getValidSortOptionForSpaceAndField(spaceInfo, field);
    const defaultDirection = option ? option.direction === 'asc' : true;
    const newClause: IOrderClause = {
      field,
      ascending: defaultDirection
    };
    newOrder = Order.withClausePrepended(existingOrder, newClause);
  }

  return newOrder;
}

/**
 * Returns the "content sort option" for the specified field. For most spaces, this comes from
 * `ContentSortOptions.ts`, but for data catalog subspaces, we look at different lists specifically
 * for catalog.
 */
function getValidSortOptionForSpaceAndField(spaceInfo: SpaceInfo, field: string): SortTool.SortOption | null {
  let list = ContentSortOptions.SortBy;

  if (isCatalogSpace(spaceInfo) || isCatalogSearchSpace(spaceInfo)) {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);
    list = contentSortOptionsByCatalogContentType[catalogContentType];
  }

  const options = ContentSortOptions.sortOptionsForValidKeysFromList([field], list);
  return options && options[0] || null;
}
